import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Section = ({ number, title, children, id }) => (
  <div id={id}>
    <p className="font-semibold text-foreground mb-1">{number}. {title}</p>
    {children}
  </div>
);

export const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Política de Privacidade – Joyn';
  }, []);

  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card border border-border rounded-xl px-8 py-10 shadow-sm">
          <div className="flex flex-col items-center mb-8 text-center">
            <img src="/logo_eventfy.png" alt="Joyn" className="w-48 h-auto object-contain mb-6" />
            <h1 className="text-xl font-semibold text-foreground">Política de Privacidade – Joyn</h1>
            <p className="text-xs text-muted-foreground mt-1">Última atualização: Outubro de 2026</p>
          </div>

          <div className="space-y-5 text-sm text-muted-foreground leading-relaxed">
            <p>
              O Joyn respeita sua privacidade e está comprometido em proteger seus dados pessoais.
              Esta Política de Privacidade explica quais informações coletamos, como elas são utilizadas
              e quais são seus direitos ao utilizar o aplicativo.
            </p>

            <Section number="1" title="Dados coletados">
              <p>Ao utilizar o Joyn, podemos coletar as seguintes informações:</p>
              <ul className="mt-2 space-y-1 list-none">
                <li>• Nome e endereço de e-mail fornecidos durante o cadastro.</li>
                <li>• Número de telefone, quando informado pelo usuário.</li>
                <li>• Foto de perfil enviada pelo usuário ou obtida através do login Google.</li>
                <li>• Localização aproximada ou precisa, mediante autorização do usuário, para exibir eventos próximos.</li>
                <li>• Informações de uso do aplicativo, como telas acessadas, interações e preferências.</li>
                <li>• Identificadores técnicos do dispositivo necessários para funcionamento, segurança e análise do aplicativo.</li>
              </ul>
            </Section>

            <Section number="2" title="Como utilizamos seus dados">
              <p>Os dados coletados podem ser utilizados para:</p>
              <ul className="mt-2 space-y-1 list-none">
                <li>• Criar e gerenciar sua conta.</li>
                <li>• Exibir eventos relevantes de acordo com sua localização.</li>
                <li>• Personalizar sua experiência dentro do aplicativo.</li>
                <li>• Permitir funcionalidades como favoritos, agenda e inscrições em eventos.</li>
                <li>• Melhorar desempenho, estabilidade e segurança do aplicativo.</li>
                <li>• Realizar análises estatísticas sobre o uso da plataforma.</li>
              </ul>
            </Section>

            <Section number="3" title="Compartilhamento de dados">
              <p>O Joyn não vende informações pessoais dos usuários.</p>
              <p className="mt-2">
                Os dados podem ser processados por provedores de serviços essenciais para o funcionamento
                da plataforma, incluindo:
              </p>
              <ul className="mt-2 space-y-1 list-none">
                <li>• Google Firebase (autenticação, banco de dados, armazenamento, analytics e notificações).</li>
                <li>• Google Maps Platform (serviços de localização e mapas).</li>
              </ul>
              <p className="mt-2">Esses serviços possuem suas próprias políticas de privacidade.</p>
            </Section>

            <Section number="4" title="Notificações">
              <p>
                O Joyn poderá enviar notificações relacionadas a eventos, atualizações da plataforma e outras
                informações relevantes. O usuário pode desativar as notificações nas configurações do dispositivo.
              </p>
            </Section>

            <Section number="5" title="Segurança">
              <p>
                Adotamos medidas técnicas e organizacionais para proteger os dados dos usuários contra acesso
                não autorizado, alteração, divulgação ou destruição.
              </p>
            </Section>

            <Section number="6" title="Retenção de dados">
              <p>
                Os dados serão mantidos enquanto forem necessários para a prestação dos serviços ou para
                cumprimento de obrigações legais.
              </p>
            </Section>

            <Section number="7" title="Exclusão da conta e dos dados">
              <p>
                O usuário pode solicitar a exclusão de sua conta e dos dados associados a qualquer momento
                através dos canais de contato informados abaixo.
              </p>
            </Section>

            <Section number="8" title="Direitos do usuário">
              <p>
                O usuário pode solicitar acesso, correção ou exclusão de seus dados pessoais, conforme
                aplicável pela legislação vigente.
              </p>
            </Section>

            <Section number="9" title="Cookies e Tecnologias de Rastreamento no Site" id="cookies">
              <p>
                Esta seção se aplica ao site joynapp.com.br. As seções anteriores desta política também
                se aplicam ao site, além do aplicativo.
              </p>
              <p className="mt-2">
                Ao visitar nosso site, utilizamos cookies e tecnologias semelhantes para garantir o
                funcionamento básico da plataforma e, mediante seu consentimento, para fins de análise
                e publicidade.
              </p>

              <p className="font-semibold text-foreground mt-4 mb-1">Cookies necessários</p>
              <p>
                Essenciais para o funcionamento do site (ex: manter sua sessão de login). Não podem ser
                desativados, pois o site não funciona corretamente sem eles.
              </p>

              <p className="font-semibold text-foreground mt-4 mb-1">Cookies de análise e publicidade (opcionais)</p>
              <p>Utilizados somente mediante seu consentimento:</p>
              <ul className="mt-2 space-y-1 list-none">
                <li>• Meta Pixel (Facebook/Instagram): usado para medir a eficácia de nossas campanhas publicitárias e otimizar anúncios.</li>
                <li>• Firebase Analytics (Google): usado para entender como os visitantes utilizam o site e melhorar a experiência. Atualmente em fase de configuração.</li>
              </ul>

              <p className="font-semibold text-foreground mt-4 mb-1">Contagem anônima de visitas e de visualizações de eventos</p>
              <p>
                Para saber quantas pessoas visitam o site e mostrar aos organizadores quantas vezes cada
                evento foi visto, contamos de forma anônima as visitas ao site e as visualizações da página
                de cada evento. Para não contar o mesmo visitante duas vezes, o site guarda no seu navegador
                (armazenamento local) apenas a data da sua última visita e a lista dos eventos que você já
                abriu, com a data de cada visita. Essas informações não contêm nome, e-mail, identificador
                ou qualquer outro dado sobre você, não são enviadas aos nossos servidores e não são usadas
                para acompanhar sua navegação. Nosso servidor recebe somente o código do evento (no caso de
                uma página de evento) e se é a primeira visita deste navegador no dia ou àquele evento, e
                soma uma unidade aos totais. O endereço IP é usado apenas por alguns minutos, em memória,
                para evitar abusos, e não é armazenado. Você pode apagar essas informações a qualquer
                momento limpando os dados do site no seu navegador.
              </p>

              <p className="font-semibold text-foreground mt-4 mb-1">Suas escolhas</p>
              <p>
                Ao acessar o site pela primeira vez, você pode aceitar todos os cookies, recusar os não
                essenciais, ou personalizar sua escolha por categoria. Você pode alterar sua decisão a
                qualquer momento através do link "Preferências de cookies", disponível no rodapé do site.
              </p>
            </Section>

            <Section number="10" title="Alterações nesta política">
              <p>
                Esta Política de Privacidade poderá ser atualizada periodicamente. Alterações relevantes
                serão comunicadas através do aplicativo ou de outros meios apropriados.
              </p>
            </Section>

            <Section number="11" title="Contato">
              <p>
                Em caso de dúvidas, solicitações relacionadas à privacidade ou pedidos de exclusão de dados,
                entre em contato:
              </p>
              <a
                href="mailto:oficialjoyn@gmail.com"
                className="text-primary hover:underline mt-1 inline-block"
              >
                oficialjoyn@gmail.com
              </a>
            </Section>

            <p className="pt-2 border-t border-border">
              Ao utilizar o Joyn, você concorda com os termos desta Política de Privacidade.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
